// src/api/chatService.js
//
// FIXED:
//   editMessage() now sends { content } (plaintext) because that's what
//   the updated MessageEditSerializer on the backend accepts. When E2EE is
//   wired, switch to sending { ciphertext, iv, signature } instead.

import apiClient from "./apiClient";

const CHAT = "/chat/api";

export const clearConversation = (convId) =>
  apiClient.post(`${CHAT}/conversations/${convId}/clear/`);

export const muteConversation = (convId, isMuted) =>
  apiClient.post(`${CHAT}/conversations/${convId}/mute/`, { is_muted: isMuted });

// ── Key Bundle ────────────────────────────────────────────────────────────────
export const uploadKeyBundle = (bundle) =>
  apiClient.post(`${CHAT}/keys/my/`, bundle);

export const getMyKeyBundle = () =>
  apiClient.get(`${CHAT}/keys/my/`);

export const getKeyBundle = (employeeId) =>
  apiClient.get(`${CHAT}/keys/${employeeId}/`);

// ── Conversations ─────────────────────────────────────────────────────────────
export const getConversations = () =>
  apiClient.get(`${CHAT}/conversations/`);

export const createDirectConversation = (otherUserId, encryptedKeys = null, convType = "DIRECT") =>
  apiClient.post(`${CHAT}/conversations/`, {
    conv_type:      convType,
    other_user_id:  otherUserId,
    encrypted_keys: encryptedKeys,
  });

export const createGroupConversation = (name, memberIds, encryptedKeys = null) =>
  apiClient.post(`${CHAT}/conversations/`, {
    conv_type:      "GROUP",
    name,
    member_ids:     memberIds,
    encrypted_keys: encryptedKeys,
  });

export const getConversationDetail = (convId) =>
  apiClient.get(`${CHAT}/conversations/${convId}/`);

export const deleteConversation = (convId) =>
  apiClient.delete(`${CHAT}/conversations/${convId}/`);

// ── Messages ──────────────────────────────────────────────────────────────────
export const getMessages = (convId, cursor = null) => {
  const params = cursor ? { cursor } : {};
  return apiClient.get(`${CHAT}/conversations/${convId}/messages/`, { params });
};

export const sendMessage = (convId, payload) =>
  apiClient.post(`${CHAT}/conversations/${convId}/messages/`, payload);

// FIXED: sends `content` (plaintext) — backend MessageEditSerializer now
// accepts this. Switch to ciphertext/iv/signature when E2EE is active.
export const editMessage = (msgId, payload) =>
  apiClient.patch(`${CHAT}/messages/${msgId}/`, payload);

export const deleteMessage = (msgId, deleteType = "everyone") =>
  apiClient.delete(`${CHAT}/messages/${msgId}/`, { data: { delete_type: deleteType } });

export const addReaction = (msgId, emoji) =>
  apiClient.post(`${CHAT}/messages/${msgId}/react/`, { emoji });

export const removeReaction = (msgId, emoji) =>
  apiClient.delete(`${CHAT}/messages/${msgId}/react/`, { data: { emoji } });

// ── Mark read ─────────────────────────────────────────────────────────────────
export const markMessagesRead = (convId, messageIds) =>
  apiClient.post(`${CHAT}/messages/read/`, {
    conversation_id: convId,
    message_ids:     messageIds,
  });

// ── Presence ──────────────────────────────────────────────────────────────────
export const getPresence = (userIds) =>
  apiClient.post(`${CHAT}/presence/`, {
    user_ids: userIds,
  });

// ── File Upload ───────────────────────────────────────────────────────────────
export const uploadAttachment = (formData, onProgress) => {
  // Ensure mandatory encrypted fields are present (even if empty) to prevent backend validation errors
  // for non-encrypted files or conversations without a key.
  const requiredFields = [
    "encrypted_file_key",
    "file_key_iv",
    "file_iv",
    "encrypted_thumb_key",
    "thumb_key_iv",
    "thumb_iv"
  ];
  
  requiredFields.forEach((field) => {
    if (!formData.has(field)) {
      formData.append(field, "");
    }
  });

  return apiClient.post(`${CHAT}/upload/`, formData, {
    onUploadProgress: (e) => {
      if (onProgress) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
};

// ── Search ────────────────────────────────────────────────────────────────────
export const searchMessages = (query, convId = null) => {
  const params = { q: query };
  if (convId) params.conv_id = convId;
  return apiClient.get(`${CHAT}/search/messages/`, { params });
};

export const searchConversations = (query) =>
  apiClient.get(`${CHAT}/search/conversations/`, { params: { q: query } });

// ── Group Members ─────────────────────────────────────────────────────────────
export const addGroupMember = (convId, memberIds) =>
  apiClient.post(`${CHAT}/conversations/${convId}/members/`, {
    member_ids: memberIds,
  });

export const removeGroupMember = (convId, employeeId) =>
  apiClient.delete(`${CHAT}/conversations/${convId}/members/${employeeId}/`);

// ── Unread counts ─────────────────────────────────────────────────────────────
export const getUnreadCounts = () =>
  apiClient.get(`${CHAT}/messages/unread-counts/`);

// ── Presence ──────────────────────────────────────────────────────────────────
export const setPresenceStatus = (status) =>
  apiClient.post(`${CHAT}/presence/status/`, { status });

// ── Live Support ──────────────────────────────────────────────────────────────
export const getSupportConversations = () =>
  apiClient.get(`${CHAT}/support/conversations/`);

export const createSupportConversation = (title) =>
  apiClient.post(`${CHAT}/support/conversations/`, { title });

export const getSupportConversationDetail = (pk) =>
  apiClient.get(`${CHAT}/support/conversations/${pk}/`);

export const updateSupportConversationStatus = (pk, status) =>
  apiClient.patch(`${CHAT}/support/conversations/${pk}/`, { status });

export const getSupportMessages = (pk) =>
  apiClient.get(`${CHAT}/support/conversations/${pk}/messages/`);

export const sendSupportMessage = (pk, content, attachmentIds = []) =>
  apiClient.post(`${CHAT}/support/conversations/${pk}/messages/`, { content, attachment_ids: attachmentIds });

// Returns { open: number, in_progress: number } — super admin only
export const getSupportStats = () =>
  apiClient.get(`${CHAT}/support/stats/`);